if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.scale = 1;
      this.pan = { x: 0, y: 0 };
      this.pointers = new Map();
    }

    setupViewer() {
      if (this.viewerReady) return;
      this.viewerReady = true;
      this.viewport = this.querySelector('.product-media-modal__content');
      this.counter = this.querySelector('[data-gallery-counter]');
      this.status = this.querySelector('[data-gallery-status]');
      this.hint = this.querySelector('[data-gallery-hint]');
      this.fitButton = this.querySelector('[data-gallery-action="fit"]');
      this.zoomInButton = this.querySelector('[data-gallery-action="zoom-in"]');
      this.zoomOutButton = this.querySelector('[data-gallery-action="zoom-out"]');
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-gallery-action]');
        if (!button || !this.contains(button)) return;
        const action = button.dataset.galleryAction;
        if (action === 'previous') this.navigate(-1);
        if (action === 'next') this.navigate(1);
        if (action === 'zoom-in') this.zoomAt(this.scale + 0.5);
        if (action === 'zoom-out') this.zoomAt(this.scale - 0.5);
        if (action === 'fit') this.zoomAt(1);
      });
      this.addEventListener('keydown', (event) => {
        if (!this.hasAttribute('open') || event.target.closest('input,textarea,select,video,iframe,model-viewer')) return;
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.navigate(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.navigate(1); }
        if (event.key === '+' || event.key === '=') { event.preventDefault(); this.zoomAt(this.scale + 0.5); }
        if (event.key === '-') { event.preventDefault(); this.zoomAt(this.scale - 0.5); }
        if (event.key === '0') { event.preventDefault(); this.zoomAt(1); }
      });
      this.viewport.addEventListener('load', (event) => {
        if (event.target === this.activeMedia) { this.measureImage(); this.updateImageState(); }
      }, true);
      this.viewport.addEventListener('error', (event) => {
        if (event.target === this.activeMedia) this.updateImageState(true);
      }, true);
      this.viewport.addEventListener('dragstart', (event) => {
        if (event.target.tagName === 'IMG') event.preventDefault();
      });
      this.viewport.addEventListener('dblclick', (event) => {
        if (!this.isImage()) return;
        event.preventDefault();
        this.zoomAt(this.scale > 1 ? 1 : 2, this.anchor(event.clientX, event.clientY));
      });
      this.viewport.addEventListener('pointerdown', (event) => this.pointerDown(event));
      this.viewport.addEventListener('pointermove', (event) => this.pointerMove(event));
      this.viewport.addEventListener('pointerup', (event) => this.pointerEnd(event));
      this.viewport.addEventListener('pointercancel', (event) => this.pointerEnd(event, true));
      this.viewport.addEventListener('lostpointercapture', (event) => {
        if (this.pointers.has(event.pointerId)) this.pointerEnd(event, true);
      });
      this.observer = new ResizeObserver(() => {
        if (this.hasAttribute('open')) this.measureImage();
      });
      this.observer.observe(this.viewport);
    }

    show(opener) {
      if (!this.classList.contains('dl-gallery-modal')) {
        super.show(opener);
        this.showActiveMedia();
        return;
      }
      this.setupViewer();
      this.pagePosition = { x: window.scrollX, y: window.scrollY };
      document.documentElement.classList.add('dl-gallery-open');
      super.show(opener);
      this.items = [...this.viewport.children].filter((item) => item.hasAttribute('data-media-id'));
      const requested = opener?.getAttribute('data-media-id');
      const index = this.items.findIndex((item) => item.dataset.mediaId === requested);
      this.select(index < 0 ? 0 : index);
      this.querySelector('.product-media-modal__toggle')?.focus({ preventScroll: true });
    }

    hide() {
      if (this.classList.contains('dl-gallery-modal')) {
        this.clearGesture();
        this.pauseMedia(this.activeMedia);
        this.resetZoom();
        document.documentElement.classList.remove('dl-gallery-open');
      }
      super.hide();
      if (this.pagePosition) window.scrollTo({ left: this.pagePosition.x, top: this.pagePosition.y, behavior: 'instant' });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.clearGesture();
      if (this.hasAttribute('open') && !document.querySelector('product-modal.dl-gallery-modal[open]')) {
        document.documentElement.classList.remove('dl-gallery-open');
        document.body.classList.remove('overflow-hidden');
      }
    }

    isImage() { return this.activeMedia?.tagName === 'IMG'; }

    select(index) {
      if (!this.items?.length) return;
      this.pauseMedia(this.activeMedia);
      this.clearGesture();
      this.index = (index + this.items.length) % this.items.length;
      this.items.forEach((item, position) => {
        const active = position === this.index;
        item.classList.toggle('active', active);
        item.setAttribute('aria-hidden', String(!active));
        item.inert = !active;
      });
      this.activeMedia = this.items[this.index];
      this.resetZoom();
      this.viewport.scrollLeft = 0;
      this.viewport.scrollTop = 0;
      this.viewport.classList.toggle('dl-gallery-image', this.isImage());
      if (this.isImage()) {
        this.activeMedia.loading = 'eager';
        this.activeMedia.draggable = false;
        this.activeMedia.sizes = '(max-width: 749px) 100vw, 90vw';
        this.measureImage();
        requestAnimationFrame(() => { if (this.hasAttribute('open')) this.measureImage(); });
        const nextImage = this.items[(this.index + 1) % this.items.length];
        if (nextImage?.tagName === 'IMG') { nextImage.loading = 'eager'; nextImage.sizes = '(max-width: 749px) 100vw, 90vw'; }
      } else {
        const template = this.activeMedia.querySelector('template');
        if (this.activeMedia.tagName === 'DEFERRED-MEDIA' && template?.content.querySelector('.js-youtube')) this.activeMedia.loadContent();
      }
      this.counter.textContent = `${this.index + 1} / ${this.items.length}`;
      this.status.textContent = `Image ${this.index + 1} of ${this.items.length}${this.activeMedia.alt ? ': ' + this.activeMedia.alt : ''}`;
      this.querySelectorAll('[data-gallery-action="previous"],[data-gallery-action="next"]').forEach((button) => {
        button.hidden = this.items.length < 2;
      });
      this.updateControls();
      this.updateImageState();
    }

    navigate(direction) { this.select(this.index + direction); }

    updateImageState(failed = false) {
      const loader = this.viewport.querySelector('[data-gallery-loading]');
      if (!loader) return;
      loader.hidden = !this.isImage() || (this.activeMedia.complete && this.activeMedia.naturalWidth > 0);
      loader.textContent = failed ? 'Image could not load. Please try another image.' : 'Loading image…';
    }

    pauseMedia(item) {
      item?.querySelectorAll('video').forEach((video) => video.pause());
      item?.querySelectorAll('iframe').forEach((frame) => {
        try {
          const origin = new URL(frame.src, location.href).origin;
          if (/youtube(?:-nocookie)?\.com$/.test(new URL(origin).hostname)) frame.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', origin);
          if (new URL(origin).hostname === 'player.vimeo.com') frame.contentWindow?.postMessage('{"method":"pause"}', origin);
        } catch (_) {}
      });
      item?.modelViewerUI?.pause?.();
    }

    resetZoom() {
      this.scale = 1;
      this.pan = { x: 0, y: 0 };
      if (this.isImage()) this.renderTransform();
    }

    measureImage() {
      if (!this.isImage() || !this.viewport.clientWidth || !this.viewport.clientHeight) return;
      const image = this.activeMedia;
      const width = image.naturalWidth || Number(image.getAttribute('width')) || 1;
      const height = image.naturalHeight || Number(image.getAttribute('height')) || 1;
      const fit = Math.min((this.viewport.clientWidth - 16) / width, (this.viewport.clientHeight - 16) / height, 1);
      this.fitSize = { width: width * fit, height: height * fit };
      image.style.width = `${this.fitSize.width}px`;
      image.style.height = `${this.fitSize.height}px`;
      this.clampPan();
      this.renderTransform();
    }

    anchor(clientX, clientY) {
      const rect = this.viewport.getBoundingClientRect();
      return { x: clientX - rect.left - rect.width / 2, y: clientY - rect.top - rect.height / 2 };
    }

    zoomAt(value, anchor = { x: 0, y: 0 }) {
      if (!this.isImage()) return;
      const next = Math.min(4, Math.max(1, value));
      const ratio = next / this.scale;
      this.pan.x = anchor.x - (anchor.x - this.pan.x) * ratio;
      this.pan.y = anchor.y - (anchor.y - this.pan.y) * ratio;
      this.scale = next;
      if (next === 1) this.pan = { x: 0, y: 0 };
      this.clampPan();
      this.renderTransform();
      this.updateControls();
    }

    clampPan() {
      if (!this.fitSize) return;
      const x = Math.max(0, (this.fitSize.width * this.scale - this.viewport.clientWidth) / 2);
      const y = Math.max(0, (this.fitSize.height * this.scale - this.viewport.clientHeight) / 2);
      this.pan.x = Math.max(-x, Math.min(x, this.pan.x));
      this.pan.y = Math.max(-y, Math.min(y, this.pan.y));
    }

    renderTransform() {
      if (!this.isImage()) return;
      this.activeMedia.style.transform = `translate(-50%, -50%) translate(${this.pan.x}px, ${this.pan.y}px) scale(${this.scale})`;
      this.viewport.classList.toggle('dl-gallery-zoomed', this.scale > 1.01);
    }

    updateControls() {
      const image = this.isImage();
      this.zoomInButton.disabled = !image || this.scale >= 4;
      this.zoomOutButton.disabled = !image || this.scale <= 1;
      this.fitButton.disabled = !image;
      this.fitButton.textContent = 'Fit';
      this.fitButton.title = `${Math.round(this.scale * 100)}% — Fit entire image to screen`;
      this.fitButton.setAttribute('aria-label', 'Fit entire image to screen');
      this.hint.textContent = !image ? 'Use arrows to browse all media' : this.scale > 1.01 ? 'Drag to move · Tap Fit to see the whole image' : 'Swipe to browse · Pinch or use + to zoom';
    }

    pointerDown(event) {
      if (!this.isImage() || event.button > 0) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.viewport.setPointerCapture(event.pointerId);
      if (this.pointers.size === 1) {
        this.gesture = { x: event.clientX, y: event.clientY, pan: { ...this.pan }, moved: false, multi: false };
      } else if (this.pointers.size === 2) {
        const points = [...this.pointers.values()];
        const middle = this.anchor((points[0].x + points[1].x) / 2, (points[0].y + points[1].y) / 2);
        this.gesture = { multi: true, distance: Math.max(1, Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y)), scale: this.scale, anchor: middle, pan: { ...this.pan } };
      }
    }

    pointerMove(event) {
      if (!this.pointers.has(event.pointerId) || !this.gesture) return;
      event.preventDefault();
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size >= 2 && this.gesture.distance) {
        const points = [...this.pointers.values()];
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        const middle = this.anchor((points[0].x + points[1].x) / 2, (points[0].y + points[1].y) / 2);
        this.scale = Math.min(4, Math.max(1, this.gesture.scale * distance / this.gesture.distance));
        const ratio = this.scale / this.gesture.scale;
        this.pan.x = middle.x - (this.gesture.anchor.x - this.gesture.pan.x) * ratio;
        this.pan.y = middle.y - (this.gesture.anchor.y - this.gesture.pan.y) * ratio;
        this.clampPan();
        this.renderTransform();
        this.updateControls();
      } else if (this.pointers.size === 1 && this.gesture.x !== undefined) {
        const dx = event.clientX - this.gesture.x, dy = event.clientY - this.gesture.y;
        if (Math.hypot(dx, dy) > 6) this.gesture.moved = true;
        if (this.scale > 1.01) {
          this.pan = { x: this.gesture.pan.x + dx, y: this.gesture.pan.y + dy };
          this.clampPan();
          this.renderTransform();
        }
      }
    }

    pointerEnd(event, canceled = false) {
      if (!this.pointers.has(event.pointerId)) return;
      const gesture = this.gesture;
      this.pointers.delete(event.pointerId);
      if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
      if (!canceled && gesture && !gesture.multi && this.pointers.size === 0 && this.scale <= 1.01) {
        const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.25) this.navigate(dx < 0 ? 1 : -1);
      }
      if (this.pointers.size === 1) {
        const remaining = [...this.pointers.values()][0];
        this.gesture = { x: remaining.x, y: remaining.y, pan: { ...this.pan }, moved: true, multi: true };
      } else if (!this.pointers.size) this.gesture = null;
    }

    clearGesture() {
      const ids = [...this.pointers.keys()];
      this.pointers.clear();
      ids.forEach((id) => { if (this.viewport?.hasPointerCapture(id)) this.viewport.releasePointerCapture(id); });
      this.gesture = null;
    }

    // Keep the original theme behavior for any modal that does not use the enhanced viewer.
    showActiveMedia() {
      this.querySelectorAll('[data-media-id]').forEach((element) => element.classList.remove('active'));
      const active = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
      if (!active) return;
      active.classList.add('active');
      active.scrollIntoView();
      const container = this.querySelector('[role="document"]');
      container.scrollLeft = (active.width - container.clientWidth) / 2;
      if (active.nodeName === 'DEFERRED-MEDIA' && active.querySelector('template')?.content.querySelector('.js-youtube')) active.loadContent();
    }
  });
}
